import {
  Component,
  input,
  AfterViewInit,
  viewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ModalService } from '@galaxy/atlas';
import { getLocations$ } from '@vendasta/protos/atlas/v1/atlas';
import { locationFromJson, locationToJson } from '@vendasta/protos/atlas/v1/codec';
import type { GetLocationsRequest, Location } from '@vendasta/protos/atlas/v1/types';
import { Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  withLatestFrom,
  takeUntil,
} from 'rxjs/operators';
import { BusinessNavConfigService, Config } from '../config.service';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { BusinessNavDataService } from '../data.service';
import { LocationSwitcherModalComponent } from '../modals/location-switcher-modal/location-switcher-modal.component';
import { BusinessNavLocationService } from '../modals/location-switcher-modal/location.service';

interface CachedLocationResponse {
  locationId: string;
  response: Location;
}

@Component({
  selector: 'bc-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LocationSelectorComponent implements AfterViewInit, OnDestroy {
  icon = input<'sync_alt' | 'arrow_drop_down'>('arrow_drop_down');
  isRounded = input<boolean>(false);
  interactable$: Observable<boolean> = this.dataService.locationSwitchable$;
  currentLocation$: Observable<Location>;
  currentLocationName$: Observable<string>;
  currentLocationAddress$: Observable<string>;
  showIcon$: Observable<boolean>;
  isLocationNameTruncated = false;

  private readonly locationNameSpan = viewChild<ElementRef<HTMLSpanElement>>('locationNameSpan');
  private readonly destroy$ = new Subject<void>();
  readonly tooltipPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
  ];

  constructor(
    private configService: BusinessNavConfigService,
    private locationService: BusinessNavLocationService,
    private dataService: BusinessNavDataService,
    private sidebarService: SidebarService,
    private modalService: ModalService,
  ) {
    this.currentLocation$ = this.configService.config$.pipe(
      filter((config) => !!config.accountId || !!config.groupPath),
      switchMap((config) => {
        const req: Partial<GetLocationsRequest> = config.groupPath
          ? { groups: { groupPaths: [config.groupPath] } }
          : { accountGroups: { accountGroupIds: [config.accountId] } };
        return getLocations$(req);
      }),
      map((locations) => {
        let cache: CachedLocationResponse;
        const location = locations.locations[0];
        if (location && location.brand) {
          cache = {
            locationId: location.brand.pathNodes.join('|'),
            response: location,
          };
        } else if (location && location.accountGroup) {
          cache = {
            locationId: location.accountGroup.accountGroupId,
            response: location,
          };
        }
        this.setLocationCache(cache);
        return cache;
      }),
      startWith(LocationSelectorComponent.getLastLocation()),
      withLatestFrom(this.configService.config$),
      filter(
        ([res, config]: [CachedLocationResponse, Config]) =>
          !!res && res.locationId === LocationSelectorComponent.locationIdFromConfig(config),
      ),
      map(([res]: [CachedLocationResponse, Config]) => res.response),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.currentLocationName$ = this.currentLocation$.pipe(
      distinctUntilChanged(),
      map((location: Location) => this.getLocationName(location)),
      filter((name: string) => !!name),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    this.currentLocationAddress$ = this.currentLocation$.pipe(
      distinctUntilChanged(),
      map((location: Location) => this.getLocationAddress(location)),
      filter((address: string) => !!address),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.showIcon$ = this.sidebarService.showMenuToggle$.pipe(
      map((isVisible: boolean) => !isVisible),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private static getLastLocation(): CachedLocationResponse {
    const o: { locationId: string; response: object } = JSON.parse(
      localStorage.getItem('business-nav-current-location'),
    );
    if (!o || !o.response) {
      return null;
    }
    return {
      locationId: o.locationId,
      response: locationFromJson(o.response as Record<string, unknown>),
    };
  }

  private static locationIdFromConfig(cfg: Config): string {
    return cfg.accountId ? cfg.accountId : cfg.groupPath;
  }

  setLocationCache(cache: CachedLocationResponse): void {
    localStorage.setItem(
      'business-nav-current-location',
      JSON.stringify({
        locationId: cache.locationId,
        response: locationToJson(cache.response),
      }),
    );
  }

  getLocationName(location: Location): string {
    if (!location) {
      return '';
    }
    if (location.accountGroup) {
      return location.accountGroup.name;
    }
    if (location.brand) {
      return location.brand.name;
    }
    return '';
  }

  getLocationAddress(location: Location): string {
    if (!location) {
      return '';
    }
    if (location.accountGroup) {
      return location.accountGroup.address;
    }
    if (location.brand) {
      return 'Group';
    }
    return '';
  }

  showSwitchLocationDialog(): void {
    this.locationService.setQuery('');
    this.modalService.openList(LocationSwitcherModalComponent);
  }

  ngAfterViewInit(): void {
    this.currentLocationName$
      .pipe(
        filter((name) => !!name),
        takeUntil(this.destroy$),
      )
      .subscribe(() => setTimeout(() => this.checkTruncation(), 0));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkTruncation(): void {
    const elementRef = this.locationNameSpan();
    if (elementRef?.nativeElement) {
      const element = elementRef.nativeElement;
      this.isLocationNameTruncated = element.scrollWidth > element.offsetWidth;
    }
  }
}
